// src/components/proposal/GenerationProgressFloat.tsx
// Floating, collapsible progress indicator for background deliverables generation

import React, { useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  X,
  Loader2,
  CheckCircle,
  AlertCircle,
  FileText,
  Shield,
  Users,
  Sparkles,
  Package,
} from 'lucide-react';

export type GenerationStep =
  | 'idle'
  | 'extracting'
  | 'enriching'
  | 'labor'
  | 'synthesizing'
  | 'complete'
  | 'error';

export interface GenerationProgress {
  step: GenerationStep;
  contractTitle: string;
  contractId: string;
  error?: string;
  result?: {
    deliverablesCount: number;
    componentsCount?: number;
  };
}

interface GenerationProgressFloatProps {
  progress: GenerationProgress | null;
  onDismiss: () => void;
  onViewResult?: () => void;
}

const STEPS = [
  { id: 'extracting', label: 'Extracting Deliverables', icon: FileText, description: 'Analyzing RFQ documents...' },
  { id: 'enriching', label: 'Enriching Compliance', icon: Shield, description: 'Adding compliance requirements...' },
  { id: 'labor', label: 'Generating Labor', icon: Users, description: 'Building labor framework...' },
  { id: 'synthesizing', label: 'Synthesizing Product', icon: Sparkles, description: 'Creating product definition...' },
];

export function GenerationProgressFloat({
  progress,
  onDismiss,
  onViewResult,
}: GenerationProgressFloatProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  if (!progress || progress.step === 'idle') return null;

  const isComplete = progress.step === 'complete';
  const isError = progress.step === 'error';
  const isRunning = !isComplete && !isError;

  const currentStepIndex = STEPS.findIndex(s => s.id === progress.step);

  // Minimized view - just a small floating pill
  if (!isExpanded) {
    return (
      <div className="fixed bottom-4 right-4 z-[60]">
        <button
          onClick={() => setIsExpanded(true)}
          className={`flex items-center gap-2 px-4 py-2 rounded-full shadow-lg transition-all hover:scale-105 ${
            isComplete
              ? 'bg-success-bg0 text-white'
              : isError
              ? 'bg-urgent-bg0 text-white'
              : 'bg-bw-primary text-white'
          }`}
        >
          {isRunning && <Loader2 className="h-4 w-4 animate-spin" />}
          {isComplete && <CheckCircle className="h-4 w-4" />}
          {isError && <AlertCircle className="h-4 w-4" />}
          <span className="text-sm font-medium">
            {isComplete
              ? 'Generation Complete'
              : isError
              ? 'Generation Failed'
              : 'Generating...'}
          </span>
          <ChevronUp className="h-4 w-4" />
        </button>
      </div>
    );
  }

  // Expanded view - full progress panel
  return (
    <div className="fixed bottom-4 right-4 z-[60] w-80 bg-bw-surface rounded-xl shadow-2xl border border-bw-border overflow-hidden">
      {/* Header */}
      <div className={`px-4 py-3 flex items-center justify-between ${
        isComplete
          ? 'bg-success-bg0'
          : isError
          ? 'bg-urgent-bg0'
          : 'bg-bw-primary'
      }`}>
        <div className="flex items-center gap-2 text-white">
          <Package className="h-5 w-5" />
          <span className="font-semibold text-sm">Deliverables Generation</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setIsExpanded(false)}
            className="p-1 hover:bg-white/20 rounded transition-colors"
            title="Minimize"
          >
            <ChevronDown className="h-4 w-4 text-white" />
          </button>
          {(isComplete || isError) && (
            <button
              onClick={onDismiss}
              className="p-1 hover:bg-white/20 rounded transition-colors"
              title="Dismiss"
            >
              <X className="h-4 w-4 text-white" />
            </button>
          )}
        </div>
      </div>

      {/* Contract Title */}
      <div className="px-4 py-2 bg-bw-surface-subtle border-b border-bw-border">
        <p className="text-xs text-bw-muted">Contract</p>
        <p className="text-sm font-medium text-bw-ink truncate">{progress.contractTitle}</p>
      </div>

      {/* Progress Steps */}
      <div className="p-4 space-y-3">
        {STEPS.map((step, index) => {
          const StepIcon = step.icon;
          const isCurrentStep = step.id === progress.step;
          const isCompletedStep = currentStepIndex > index || isComplete;
          const isPendingStep = currentStepIndex < index && !isComplete;

          return (
            <div
              key={step.id}
              className={`flex items-center gap-3 ${
                isPendingStep ? 'opacity-40' : ''
              }`}
            >
              <div
                className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                  isCompletedStep
                    ? 'bg-success-bg text-success'
                    : isCurrentStep
                    ? 'bg-bw-primary-tint text-bw-primary'
                    : 'bg-bw-surface-subtle text-bw-muted'
                }`}
              >
                {isCompletedStep ? (
                  <CheckCircle className="h-4 w-4" />
                ) : isCurrentStep ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <StepIcon className="h-4 w-4" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p
                  className={`text-sm font-medium ${
                    isCompletedStep
                      ? 'text-success'
                      : isCurrentStep
                      ? 'text-bw-primary'
                      : 'text-bw-muted'
                  }`}
                >
                  {step.label}
                </p>
                {isCurrentStep && (
                  <p className="text-xs text-bw-muted">{step.description}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Error Message */}
      {isError && progress.error && (
        <div className="px-4 pb-4">
          <div className="bg-urgent-bg border border-urgent-border rounded-lg p-3">
            <p className="text-sm text-urgent">{progress.error}</p>
          </div>
        </div>
      )}

      {/* Success Result */}
      {isComplete && progress.result && (
        <div className="px-4 pb-4">
          <div className="bg-success-bg border border-success-border rounded-lg p-3">
            <p className="text-sm text-success">
              Generated {progress.result.deliverablesCount} deliverables
              {progress.result.componentsCount !== undefined && (
                <> with {progress.result.componentsCount} product components</>
              )}
            </p>
          </div>
          {onViewResult && (
            <button
              onClick={onViewResult}
              className="mt-3 w-full py-2 px-4 bg-success hover:brightness-110 text-white text-sm font-medium rounded-lg transition-colors"
            >
              View Results
            </button>
          )}
        </div>
      )}

      {/* Running indicator */}
      {isRunning && (
        <div className="px-4 pb-4">
          <div className="bg-bw-primary-tint border border-bw-primary/20 rounded-lg p-3 flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-bw-primary" />
            <p className="text-sm text-bw-primary">
              You can minimize this and continue working...
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default GenerationProgressFloat;
